import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { IconSrc, Icons } from 'folds';
import { MatrixEvent } from 'matrix-js-sdk';
import { IMemberContent, Membership } from '../../types/matrix/room';
import { getMxIdLocalPart } from '../utils/matrix';
import { isMembershipChanged } from '../utils/room';

export type ParsedResult = {
  icon: IconSrc;
  body: ReactNode;
};

export type MemberEventParser = (mEvent: MatrixEvent, t: TFunction) => ParsedResult;

export const parseMemberEvent: MemberEventParser = (mEvent, t) => {
  const content = mEvent.getContent<IMemberContent>();
  const prevContent = mEvent.getPrevContent() as IMemberContent;
  const senderId = mEvent.getSender();
  const userId = mEvent.getStateKey();

  if (!senderId || !userId)
    return {
      icon: Icons.User,
      body: t('Organisms.MemberEvent.broken_event'),
    };

  const senderName = getMxIdLocalPart(senderId);
  const userName = content.displayname || getMxIdLocalPart(userId);

  if (isMembershipChanged(mEvent)) {
    if (content.membership === Membership.Invite) {
      if (prevContent.membership === Membership.Knock) {
        return {
          icon: Icons.ArrowGoRightPlus,
          body: (
            <>
              <b>{senderName}</b>
              {t('Organisms.MemberEvent.accepted_join_request', { user: userName })}
              {content.reason}
            </>
          ),
        };
      }

      return {
        icon: Icons.ArrowGoRightPlus,
        body: (
          <>
            <b>{senderName}</b>
            {t('Organisms.MemberEvent.invited', { user: userName })} {content.reason}
          </>
        ),
      };
    }

    if (content.membership === Membership.Knock) {
      return {
        icon: Icons.ArrowGoRightPlus,
        body: (
          <>
            <b>{userName}</b>
            {t('Organisms.MemberEvent.request_to_join', {user: userName})}
            {content.reason}
          </>
        ),
      };
    }

    if (content.membership === Membership.Join) {
      return {
        icon: Icons.ArrowGoRight,
        body: (
          <>
            <b>{userName}</b>
            {t('Organisms.MemberEvent.joined_the_room')}
          </>
        ),
      };
    }

    if (content.membership === Membership.Leave) {
      if (prevContent.membership === Membership.Invite) {
        return {
          icon: Icons.ArrowGoRightCross,
          body:
            senderId === userId ? (
              <>
                <b>{userName}</b>
                {t('Organisms.MemberEvent.rejected_invitation')}
                {content.reason}
              </>
            ) : (
              <>
                <b>{senderName}</b>
                {t('Organisms.MemberEvent.rejected_join_request', { user: userName })}
                {content.reason}
              </>
            ),
        };
      }

      if (prevContent.membership === Membership.Knock) {
        return {
          icon: Icons.ArrowGoRightCross,
          body:
            senderId === userId ? (
              <>
                <b>{userName}</b>
                {t('Organisms.MemberEvent.revoked_join_request')}
                {content.reason}
              </>
            ) : (
              <>
                <b>{senderName}</b>
                {t('Organisms.MemberEvent.revoked_invite', { user: userName })}
                {content.reason}
              </>
            ),
        };
      }

      if (prevContent.membership === Membership.Ban) {
        return {
          icon: Icons.ArrowGoLeft,
          body: (
            <>
              <b>{senderName}</b>
              {t('Organisms.MemberEvent.unbanned', { user: userName })} {content.reason}
            </>
          ),
        };
      }

      return {
        icon: Icons.ArrowGoLeft,
        body:
          senderId === userId ? (
            <>
              <b>{userName}</b>
              {t('Organisms.MemberEvent.left_the_room')}
              {content.reason}
            </>
          ) : (
            <>
              <b>{senderName}</b>
              {t('Organisms.MemberEvent.kicked', { user: userName })} {content.reason}
            </>
          ),
      };
    }

    if (content.membership === Membership.Ban) {
      return {
        icon: Icons.ArrowGoLeft,
        body: (
          <>
            <b>{senderName}</b>
            {t('Organisms.MemberEvent.banned', { user: userName })} {content.reason}
          </>
        ),
      };
    }
  }

  if (content.displayname !== prevContent.displayname) {
    const prevUserName = prevContent.displayname || userId;

    return {
      icon: Icons.Mention,
      body: content.displayname ? (
        <>
          <b>{prevUserName}</b>
          {t('Organisms.MemberEvent.changed_display_name')}
          <b>{userName}</b>
        </>
      ) : (
        <>
          <b>{prevUserName}</b>
          {t('Organisms.MemberEvent.removed_display_name')}
        </>
      ),
    };
  }
  if (content.avatar_url !== prevContent.avatar_url) {
    return {
      icon: Icons.User,
      body: content.displayname ? (
        <>
          <b>{userName}</b>
          {t('Organisms.MemberEvent.changed_avatar')}
        </>
      ) : (
        <>
          <b>{userName}</b>
          {t('Organisms.MemberEvent.removed_avatar')}
        </>
      ),
    };
  }

  return {
    icon: Icons.User,
    body: t('Organisms.MemberEvent.broken_event'),
  };
};
